/**
 * Pure helpers for the `/pitched` tracker — grouping items by source boss and computing the
 * "n/10 Pitched · m/6 Brilliant" header totals. Kept separate from the components so they can be
 * unit tested without rendering anything (same split as `bossGrouping.ts`).
 *
 * REV17 (docs/specs/REV17-pitched-drop-counters.md) adds the lifetime-drop-counter rollup/luckiest
 * helpers below, same split.
 */
import type { BossDto, PitchedItemDto } from './reference';
import type { PitchedDropCountDto } from './pitched';

/**
 * REV17 — bosses that drop the Chaos Black Accessory Box (random Pitched-accessory pool), on top
 * of every `pitched_item.source_boss_slug`. Mirrors `PitchedService.BOX_DROPPER_BOSS_SLUGS`
 * (backend) — this exact six-boss list previously existed only as prose in this file's sibling
 * `PitchedItemRow.tsx`'s "Box" chip tooltip ("Also drops from the Chaos Black Accessory Box
 * (Kaling, Malefic Star, Bellona, Limbo, Baldrix, Jupiter)"), REV6's only record of it. No
 * shared BE/FE module in this codebase, so both copies are kept in sync by hand like every other
 * duplicated reference constant.
 */
export const BOX_DROPPER_BOSS_SLUGS = ['kaling', 'malefic-star', 'bellona', 'limbo', 'baldrix', 'jupiter'] as const;

/** REV17 — every boss slug a character can log a lifetime drop count against. */
export function countableBossSlugs(items: PitchedItemDto[]): Set<string> {
  const slugs = new Set(items.map((i) => i.sourceBossSlug));
  for (const slug of BOX_DROPPER_BOSS_SLUGS) {
    slugs.add(slug);
  }
  return slugs;
}

export interface PitchedBossGroup {
  bossSlug: string;
  bossName: string;
  items: PitchedItemDto[];
}

/**
 * Pitched-set items grouped by source boss, ordered by the boss catalog's own order (the same
 * order `groupBossesByTier`/the boss matrix ultimately derive from). A boss with no Pitched items
 * (most of the catalog) contributes no group; a source boss slug with no catalog match (should not
 * happen — every `pitched_item.source_boss_slug` is FK'd to `boss`) is skipped defensively.
 */
export function groupPitchedItemsByBoss(items: PitchedItemDto[], bossCatalog: BossDto[]): PitchedBossGroup[] {
  const pitchedItems = items.filter((i) => i.setKey === 'PITCHED');
  const groups: PitchedBossGroup[] = [];
  for (const boss of bossCatalog) {
    const bossItems = pitchedItems.filter((i) => i.sourceBossSlug === boss.slug);
    if (bossItems.length > 0) {
      groups.push({ bossSlug: boss.slug, bossName: boss.name, items: bossItems });
    }
  }
  return groups;
}

/** Brilliant-set items in boss-catalog order (a single flat list — REV6 spec: one heading, not sub-grouped). */
export function orderBrilliantItems(items: PitchedItemDto[], bossCatalog: BossDto[]): PitchedItemDto[] {
  const brilliantItems = items.filter((i) => i.setKey === 'BRILLIANT');
  const bossRank = new Map(bossCatalog.map((b, index) => [b.slug, index]));
  return [...brilliantItems].sort(
    (a, b) => (bossRank.get(a.sourceBossSlug) ?? 999) - (bossRank.get(b.sourceBossSlug) ?? 999),
  );
}

/** The source boss's display name for an item — used as Brilliant-list microtext (not repeated per-group there). */
export function bossNameFor(bossCatalog: BossDto[], bossSlug: string): string {
  return bossCatalog.find((b) => b.slug === bossSlug)?.name ?? bossSlug;
}

export interface PitchedTotals {
  pitchedObtained: number;
  pitchedTotal: number;
  brilliantObtained: number;
  brilliantTotal: number;
}

/**
 * "n/10 Pitched · m/6 Brilliant" — retired items (Black Heart) are excluded from both the Pitched
 * numerator and denominator (spec: "never counted in 'remaining' tallies"), even if a character has
 * one obtained as a legacy item.
 */
export function pitchedTotals(items: PitchedItemDto[], obtainedSlugs: string[]): PitchedTotals {
  const obtained = new Set(obtainedSlugs);
  const activePitched = items.filter((i) => i.setKey === 'PITCHED' && !i.retired);
  const brilliant = items.filter((i) => i.setKey === 'BRILLIANT');
  return {
    pitchedObtained: activePitched.filter((i) => obtained.has(i.slug)).length,
    pitchedTotal: activePitched.length,
    brilliantObtained: brilliant.filter((i) => obtained.has(i.slug)).length,
    brilliantTotal: brilliant.length,
  };
}

// ---------------------------------------------------------------------------------------------
// REV17 — lifetime drop counter rollups. Independent of the obtained toggles above; every helper
// here only ever reads `dropCounts` (character × boss × count), never `obtainedSlugs`.
// ---------------------------------------------------------------------------------------------

/** One character's lifetime total across every boss it has a drop count for. */
export function characterDropTotal(dropCounts: PitchedDropCountDto[]): number {
  return dropCounts.reduce((sum, d) => sum + d.count, 0);
}

/** The whole roster's lifetime total (sum of every character's total). */
export function rosterDropTotal(characters: { dropCounts: PitchedDropCountDto[] }[]): number {
  return characters.reduce((sum, c) => sum + characterDropTotal(c.dropCounts), 0);
}

export interface BossDropTotal {
  bossSlug: string;
  bossName: string;
  total: number;
}

/** Per-boss sums across the whole roster, in boss-catalog order, zero-total bosses omitted. */
export function perBossDropTotals(
  characters: { dropCounts: PitchedDropCountDto[] }[],
  bossCatalog: BossDto[],
): BossDropTotal[] {
  const sums = new Map<string, number>();
  for (const character of characters) {
    for (const drop of character.dropCounts) {
      sums.set(drop.bossSlug, (sums.get(drop.bossSlug) ?? 0) + drop.count);
    }
  }
  const totals: BossDropTotal[] = [];
  for (const boss of bossCatalog) {
    const total = sums.get(boss.slug) ?? 0;
    if (total > 0) {
      totals.push({ bossSlug: boss.slug, bossName: boss.name, total });
    }
  }
  return totals;
}

export interface LuckiestCharacter {
  characterId: number;
  name: string;
  total: number;
}

/**
 * The roster character with the highest lifetime drop total. `characters` must already be in
 * roster order — ties keep the first (highest-ranked) character seen, since `best` is only
 * replaced on a strictly greater total. `null` when every character's total is 0 (nothing to
 * brag about yet).
 */
export function luckiestCharacter(
  characters: { characterId: number; name: string; dropCounts: PitchedDropCountDto[] }[],
): LuckiestCharacter | null {
  let best: LuckiestCharacter | null = null;
  for (const character of characters) {
    const total = characterDropTotal(character.dropCounts);
    if (total > 0 && (best === null || total > best.total)) {
      best = { characterId: character.characterId, name: character.name, total };
    }
  }
  return best;
}
